import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { parse } from "tldts";

import {
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  LANGUAGE_KEY,
} from "constants/common";
import { ROOT_ACCESS_DOMAIN } from "constants/env";

export const API_URLS = {
  COMMON_API_URL: import.meta.env.COMMON_API_URL,
  CALCULATOR: import.meta.env.COMMON_CALCULATOR_API_URL,
} as const;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

export const getDomainOrSubdomain = (hostname: string) => {
  if (!hostname) return null;

  const cleanedHostname = hostname.replace(/^https?:\/\//, "");

  if (cleanedHostname === "localhost") {
    return ROOT_ACCESS_DOMAIN;
  }

  const host = cleanedHostname.split(":")[0].toLowerCase();

  let parts = host.split(".").filter(Boolean);

  // remove www
  if (parts[0] === "www") {
    parts.shift();
  }

  const rootIndex = parts.findIndex((p) => p === ROOT_ACCESS_DOMAIN);

  // Если это НЕ immoinvestrechner-домен
  if (rootIndex === -1) {
    const parsedHref = parse(window.location.href);

    return parsedHref.domainWithoutSuffix;
  }

  // Есть что-то перед immoinvestrechner
  if (rootIndex > 0) {
    const beforeRoot = parts.slice(0, rootIndex);

    // если структура типа dev.immoinvestrechner.de -> вернуть immoinvestrechner
    if (beforeRoot.length === 1 && beforeRoot[0] === "dev") {
      return ROOT_ACCESS_DOMAIN;
    }

    // fourlife.dev.immoinvestrechner.de -> fourlife
    return beforeRoot[0];
  }

  // immoinvestrechner.de
  return ROOT_ACCESS_DOMAIN;
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const interceptResponseSuccess = (response: any) => response.data;

const interceptResponseError = async (error: AxiosError) => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry?: boolean;
  };

  if (!error.response) {
    return Promise.reject({ message: "Server Error" });
  }

  if (
    error.response.status === 401 &&
    !originalRequest.url?.includes("/auth/refresh") &&
    !originalRequest._retry
  ) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }

          const axiosInstance = originalRequest.baseURL?.includes(
            API_URLS.CALCULATOR
          )
            ? api.calculator
            : api.common;

          return axiosInstance(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshTokenValue) {
      console.warn("No refresh token found, logging out");
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      processQueue(error, null);
      isRefreshing = false;
      return Promise.reject(error.response.data);
    }

    try {
      const { data } = await axios.post(
        `${API_URLS.COMMON_API_URL}/api/v1/auth/refresh`,
        { refreshToken: refreshTokenValue },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!data.token) {
        console.error("Refresh token response missing token:", {
          fullResponse: data,
        });

        throw new Error("Invalid refresh token response");
      }

      localStorage.setItem(AUTH_TOKEN_KEY, data.token);

      if (data.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      }

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
      }

      processQueue(null, data.token);
      isRefreshing = false;

      const axiosInstance = originalRequest.baseURL?.includes(
        API_URLS.CALCULATOR
      )
        ? api.calculator
        : api.common;

      return axiosInstance(originalRequest);
    } catch (refreshError: any) {
      const isRefreshTokenInvalid =
        refreshError?.response?.status === 401 ||
        refreshError?.response?.status === 403;

      console.error("Refresh token error:", {
        status: refreshError?.response?.status,
        statusText: refreshError?.response?.statusText,
        data: refreshError?.response?.data,
        isRefreshTokenInvalid,
        error: refreshError,
      });

      if (isRefreshTokenInvalid) {
        console.warn("Refresh token is invalid (401/403), logging out");
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        processQueue(refreshError, null);
      } else {
        console.warn(
          "Refresh token request failed (network/server error), keeping tokens"
        );
        processQueue(refreshError, null);
      }

      isRefreshing = false;

      return Promise.reject(error.response.data);
    }
  }

  return Promise.reject(error.response.data);
};

const interceptRequest = (config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const language = localStorage.getItem(LANGUAGE_KEY) || "de";

  config.headers.set(
    "x-access-domain",
    getDomainOrSubdomain(window.location.hostname)
  );
  config.headers.set("x-access-service", ROOT_ACCESS_DOMAIN); //FIXME: add new env variable for service

  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }

  config.headers.set("x-lang", language);
  // config.headers.set('ngrok-skip-browser-warning', true);

  return config;
};

const createAxiosInstance = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
  });

  instance.interceptors.request.use(interceptRequest);
  instance.interceptors.response.use(
    interceptResponseSuccess,
    interceptResponseError
  );

  return instance;
};

export const api = {
  common: createAxiosInstance(API_URLS.COMMON_API_URL),
  calculator: createAxiosInstance(API_URLS.CALCULATOR),
} as const;

export type ApiClient = typeof api;
